import {
    AnyAction,
    createAsyncThunk,
    createSlice,
    PayloadAction,
    ThunkDispatch,
} from "@reduxjs/toolkit"

import { GlobalState } from "../../store"
import { addErrorNotification } from "../notifications/actions"
import { fetchFormattingSettings } from "../common/userFormattingSettings/slice"

import { Fields } from "."
import { fieldsAPI } from "../../apis"
import { attachmentsReducersBuilder } from "./components/attachments/thunks"
import { RequestErrorDataApi } from "../../errors"

declare let window: OldUIContext.TenForceWindow

interface SetFeedbackKey {
    fieldId: string
    feedbackKey: string
}

export const fieldsSlice = createSlice({
    name: "fields",

    initialState: {
        fields: {},
        feedbackKeys: {},
        isInitialized: false,
        isLoading: false,
        itemId: -1,
    } as Fields.Item,

    reducers: {
        setItemId: (state, action: PayloadAction<number>) => {
            if (state.itemId < 0) {
                state.itemId = action.payload
            }
        },
        setFeedbackKey: (state, action: PayloadAction<SetFeedbackKey>) => {
            const {
                payload: { feedbackKey, fieldId },
            } = action
            state.feedbackKeys[fieldId] = feedbackKey
        },
        setFields(state, action: PayloadAction<Fields.Field[]>) {
            const fields = action.payload
            const newFields: Record<string, Fields.Field> = {}

            fields.map((field: Fields.Field) => {
                if (!!field.fieldId) {
                    newFields[field.fieldId] = field
                }
            })

            state.fields = newFields
            state.isLoading = false
            state.isInitialized = true
            state.isFetchingFields = false
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchFields.pending, (state) => {
                state.isFetchingFields = true
                state.isLoading = true
            })
            .addCase(fetchFields.fulfilled, (state, action) => {
                const fields = action.payload
                const newFields: Record<string, Fields.Field> = {}

                fields.map((field: Fields.Field) => {
                    if (!!field.fieldId) {
                        newFields[field.fieldId] = field
                    }
                })

                state.fields = newFields
                state.isLoading = false
                state.isInitialized = true
                state.isFetchingFields = false
            })

        builder
            .addCase(updateField.pending, (state) => {
                state.isFetchingFields = true
                state.isLoading = true
            })
            .addCase(updateField.fulfilled, (state, action) => {
                const field = action.payload
                state.fields[field.fieldId] = {
                    ...state.fields[field.fieldId],
                    value: field.value,
                }
                state.isLoading = false
                state.isFetchingFields = false
            })

        attachmentsReducersBuilder(builder)
    },
})

export const { setItemId, setFeedbackKey, setFields } = fieldsSlice.actions

export default fieldsSlice.reducer

export const defaultRequestingFieldsErrorMessage =
    "Something went wrong when fetching fields."

export const fetchFields = createAsyncThunk(
    "fields/fetchFieldsByItemId",
    async (_, { getState, dispatch }) => {
        const state = getState() as GlobalState

        const { itemId } = state.fields
        let userFormattingSettings = state.userFormattingSettings

        if (!userFormattingSettings.isInitialized) {
            const response = await dispatch(fetchFormattingSettings())
            userFormattingSettings =
                response.payload as UserMenu.UserFormattingSettings
        }

        let fields: Fields.Field[] = []

        try {
            fields = await fieldsAPI.getFields(itemId, userFormattingSettings)
        } catch (error) {
            console.error(error)
            dispatch(addErrorNotification(defaultRequestingFieldsErrorMessage))
        }

        return fields
    },
    {
        condition: (_, { getState }) => {
            const { fields } = getState() as GlobalState
            if (fields.itemId === -1 || fields.isFetchingFields) {
                // no react fields are present or
                // fetching is in progress
                return false
            }
        },
    },
)

export const defaultUpdatingFieldErrorMessage =
    "Something went wrong when updating field "
export const defaultUpdatingOtherFieldsErrorMessage =
    "Something went wrong when updating other fields. "

interface UpdateField {
    field: Fields.Field
    newValue: unknown
}

export const handleFieldUpdateFeedback = async (
    feedbackKeys: Record<string, string>,
    field: Fields.Field,
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
): Promise<void> => {
    if (!!feedbackKeys[field.fieldId]) {
        // we have to start an IPE feedback

        const feedbackProps = {
            key: feedbackKeys[field.fieldId],
            // we ignore because we've already refetched the react fields
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            value: function (): void {},
            error: (error: unknown): void => {
                console.error(error)
                dispatch(
                    addErrorNotification(
                        defaultUpdatingOtherFieldsErrorMessage,
                    ),
                )
            },
        }
        console.log(feedbackProps)
        window.feedback.register(feedbackProps)
    }
}

export const updateField = createAsyncThunk(
    "fields/updateField",
    async ({ field, newValue }: UpdateField, { getState, dispatch }) => {
        const state = getState() as GlobalState
        const { itemId, feedbackKeys } = state.fields

        // in case of an error, we'll return the old value
        let newField = field
        console.log(feedbackKeys)

        try {
            // otherwise we'll return the new field having the new value
            newField = await fieldsAPI.updateFieldValue(itemId, {
                ...field,
                value: newValue,
            })

            console.log("afterUpdate")

            // make sure we refetch all fields after we got the updated value of this field
            dispatch(fetchFields())
            await handleFieldUpdateFeedback(feedbackKeys, field, dispatch)
        } catch (error) {
            let errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}.`
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof RequestErrorDataApi) {
                errorMessage = `${error.message}`
            } else if (error instanceof Error) {
                errorMessage = `${errorMessage}. ${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        }
        return newField
    },
)
